import { useQuery } from "convex/react";
import { CopyIcon } from "lucide-react";
import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { formatRelativeTime } from "@/lib/time";
import { api } from "../../../convex/_generated/api";
import { MessageTimeline } from "./MessageTimeline";
import { ThreadContext } from "./ThreadContext";
import type { ThreadChild } from "./types";

export function ThreadDetail({
	clericalThreadId,
}: {
	clericalThreadId: string;
}) {
	const thread = useQuery(api.agent.queries.getThread, {
		clericalThreadId,
	});

	const childThreadMap = useMemo(() => {
		const map = new Map<string, ThreadChild>();
		if (thread?.children) {
			for (const child of thread.children) {
				map.set(child.clericalThreadId, child);
			}
		}
		return map;
	}, [thread?.children]);

	if (thread === undefined) {
		return (
			<div className="flex h-full items-center justify-center text-sm text-muted-foreground">
				Loading thread...
			</div>
		);
	}

	if (thread === null) {
		return (
			<div className="flex h-full items-center justify-center text-sm text-muted-foreground">
				Thread not found.
			</div>
		);
	}

	return (
		<ScrollArea className="h-full">
			<div className="mx-auto flex max-w-4xl flex-col gap-6 p-6">
				{/* Header */}
				<div>
					<div className="flex flex-wrap items-center gap-2">
						<h1 className="text-xl font-semibold">Thread</h1>
						{thread.kind && <Badge variant="secondary">{thread.kind}</Badge>}
					</div>
					<div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
						<Button
							variant="ghost"
							size="xs"
							className="font-mono text-xs text-muted-foreground hover:text-foreground"
							onClick={() =>
								navigator.clipboard.writeText(thread.clericalThreadId)
							}
						>
							{thread.clericalThreadId}
							<CopyIcon className="size-3" />
						</Button>
						<span>·</span>
						<span>Created {formatRelativeTime(thread._creationTime)}</span>
						{thread.statusText && (
							<>
								<span>·</span>
								<span>{thread.statusText}</span>
							</>
						)}
					</div>
					{thread.children.length > 0 && (
						<p className="mt-1 text-xs text-muted-foreground">
							{thread.children.length} sub-thread
							{thread.children.length !== 1 ? "s" : ""}
						</p>
					)}
				</div>

				{/* Context */}
				{thread.context && <ThreadContext context={thread.context} />}

				<Separator />

				{/* Messages */}
				<section className="space-y-2">
					<h2 className="text-sm font-semibold">Messages</h2>
					<MessageTimeline
						clericalThreadId={thread.clericalThreadId}
						childThreads={childThreadMap}
					/>
				</section>
			</div>
		</ScrollArea>
	);
}
